import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  NotificationConfigCreatedEnvelope,
  NotificationConfigUpdatedEnvelope,
  ResolveNotificationConfigResponse,
  TriggerNotificationOptions,
  UpsertNotificationConfig,
  UpsertNotificationConfigResponse,
  resolveNotificationConfig as universalResolveNotificationConfig,
  triggerNotification as universalTriggerNotification,
  upsertNotificationConfig as universalUpsertNotificationConfig,
} from './events-notifications-v2-notification-config-notifications.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function resolveNotificationConfig(
  httpClient: HttpClient
): ResolveNotificationConfigSignature {
  return (notificationConfigId: string) =>
    universalResolveNotificationConfig(
      notificationConfigId,
      // @ts-ignore
      { httpClient }
    );
}

interface ResolveNotificationConfigSignature {
  /**
   * Resolves a NotificationConfig by id. Returns saved value or default value if not saved yet.
   * @param - Id of the NotificationConfig to retrieve
   */
  (notificationConfigId: string): Promise<
    NonNullablePaths<
      ResolveNotificationConfigResponse,
      | `notificationConfig.rsvpConfirmation.notificationType`
      | `notificationConfig.rsvpConfirmation.reminderTime`,
      4
    >
  >;
}

export function upsertNotificationConfig(
  httpClient: HttpClient
): UpsertNotificationConfigSignature {
  return (_id: string, notificationConfig: UpsertNotificationConfig) =>
    universalUpsertNotificationConfig(
      _id,
      notificationConfig,
      // @ts-ignore
      { httpClient }
    );
}

interface UpsertNotificationConfigSignature {
  /**
   * Upsert a NotificationConfig
   * @param - Event ID.
   */
  (_id: string, notificationConfig: UpsertNotificationConfig): Promise<
    NonNullablePaths<
      UpsertNotificationConfigResponse,
      | `notificationConfig.rsvpConfirmation.notificationType`
      | `notificationConfig.rsvpConfirmation.reminderTime`,
      4
    >
  >;
}

export function triggerNotification(
  httpClient: HttpClient
): TriggerNotificationSignature {
  return (options: TriggerNotificationOptions) =>
    universalTriggerNotification(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TriggerNotificationSignature {
  /**
   * Triggers notification
   */
  (options: TriggerNotificationOptions): Promise<void>;
}

export const onNotificationConfigCreated = EventDefinition(
  'wix.events.notifications.v2.notification_config_created',
  true,
  (event: NotificationConfigCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<NotificationConfigCreatedEnvelope>();
export const onNotificationConfigUpdated = EventDefinition(
  'wix.events.notifications.v2.notification_config_updated',
  true,
  (event: NotificationConfigUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
      ])
    )
)<NotificationConfigUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  BaseEventMetadata,
  CheckIn,
  CommonAddress,
  CommonAddressLocation,
  CommonAddressStreetOneOf,
  CommonStreetAddress,
  CommonSubdivision,
  CouponDiscount,
  Discount,
  DiscountItem,
  DiscountItemDiscountOneOf,
  DomainEvent,
  DomainEventBodyOneOf,
  EmailNotificationConfig,
  EmailNotificationReminderTime,
  EmailNotificationType,
  EmailTemplate,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventCanceled,
  EventCopied,
  EventDeleted,
  EventGuests,
  EventMetadata,
  EventStatus,
  Fee,
  FeeName,
  FeeType,
  FormResponse,
  FormattedAddress,
  IdentificationData,
  IdentificationDataIdOneOf,
  InputValue,
  Invoice,
  Item,
  Link,
  Location,
  LocationType,
  MapCoordinates,
  MessageEnvelope,
  Money,
  NotificationConfig,
  NotificationConfigCreatedEnvelope,
  NotificationConfigUpdatedEnvelope,
  Occurrence,
  OnlineConferencingLogin,
  OrderCanceled,
  OrderConfirmed,
  OrderEmailAdded,
  OrderGuest,
  OrderPaid,
  OrderStatus,
  PaidPlanDiscount,
  PaidPlanDiscountDiscountOneOf,
  PercentDiscount,
  PushNotificationType,
  Recurrences,
  ResolveEmailNotificationConfigRequest,
  ResolveEmailNotificationConfigResponse,
  ResolveNotificationConfigRequest,
  ResolveNotificationConfigResponse,
  RestoreInfo,
  RsvpGuest,
  ScheduleConfig,
  StandardDetails,
  Status,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  Tax,
  TaxType,
  Ticket,
  TicketGuest,
  TicketPdfResolveDelayed,
  TicketPdfResolved,
  TriggerNotificationOptions,
  TriggerNotificationOptionsGuestsOneOf,
  TriggerNotificationOptionsNotificationOneOf,
  TriggerNotificationRequest,
  TriggerNotificationRequestGuestsOneOf,
  TriggerNotificationRequestNotificationOneOf,
  TriggerNotificationResponse,
  UpsertNotificationConfig,
  UpsertNotificationConfigRequest,
  UpsertNotificationConfigResponse,
  WebhookIdentityType,
} from './events-notifications-v2-notification-config-notifications.universal.js';
